import {
  Card,
  FormLayout,
  Checkbox,
  TextField,
  Select,
  Text,
  BlockStack,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { EventBookType, Event } from "@features/events/types";
import { WebsiteField } from "@shared/ui";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import TicketsList from "./tickets/TicketsList";

interface EventPaymentCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

export default function EventPaymentCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: EventPaymentCardProps) {
  const { t } = useTranslation();
  const { eventId } = useParams<{
    eventOrganizerId: string;
    eventId: string;
  }>();

  // Check if this is a new event (no eventId or eventData.id)
  const isNewEvent = !eventId || !eventData.id;

  // Currency options
  const currencyOptions = useMemo(
    () => [
      { label: EVENT_CURRENCY_DEFAULT, value: EVENT_CURRENCY_DEFAULT },
    ],
    []
  );

  const isOnlinePayments = eventData.book === EventBookType.PAY;

  // Handle paid event toggle
  const handleBookingTypeEventChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Checked: book = "pay", bookRedirectLink = null
        onDataChange({ 
          book: EventBookType.PAY, 
          bookRedirectLink: null 
        });
      } else {
        // Unchecked: book = "redirect", bookRedirectLink = valid URL (keep existing or set default)
        onDataChange({ 
          book: EventBookType.REDIRECT, 
          bookRedirectLink: eventData.bookRedirectLink || "" 
        });
      }
    },
    [onDataChange, eventData.bookRedirectLink]
  );


  // Handle max attendees change
  const handleMaxAttendeesChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        onDataChange({ spotsMax: numValue });
      }
    },
    [onDataChange]
  );

  // Handle currency change
  const handleCurrencyChange = useCallback(
    (value: string) => {
      onDataChange({ priceCurrency: value });
    },
    [onDataChange]
  );

  // Handle external ticketing link change
  const handleExternalLinkChange = useCallback(
    (value: string) => {
      onDataChange({ bookRedirectLink: value });
    },
    [onDataChange]
  );

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("eventDetails.payment.title")}
        </Text>

        <FormLayout>
          <Checkbox
            label={t("eventDetails.payment.paidEvent")}
            checked={true}
            disabled={true}
          />

          <TextField
            type="number"
            label={t("eventDetails.payment.maxAttendees")}
            value={eventData.spotsMax?.toString() || ""}
            onChange={handleMaxAttendeesChange}
            helpText={t("eventDetails.payment.maxAttendeesHelp")}
            error={fieldErrors.spotsMax}
            autoComplete="off"
          />

          <BlockStack gap="400">
            <Select
              label={t("eventDetails.payment.currency")}
              options={currencyOptions}
              value={eventData.priceCurrency || EVENT_CURRENCY_DEFAULT}
              onChange={handleCurrencyChange}
              helpText={t("eventDetails.payment.currencyHelp")}
              disabled={true}
            />

            <Checkbox
              label={t("eventDetails.payment.enableOnlinePayments")}
              checked={isOnlinePayments}
              onChange={handleBookingTypeEventChange}
              helpText={t("eventDetails.payment.onlinePaymentsHelp")}
            />

            {!isOnlinePayments && (
              <WebsiteField
                label={t("eventDetails.payment.bookRedirectLink")}
                value={eventData.bookRedirectLink || ""}
                onChange={handleExternalLinkChange}
                helpText={t("eventDetails.payment.externalLinkHelp")}
                placeholder={t("eventDetails.payment.bookRedirectPlaceholder")}
                error={
                  !isOnlinePayments && 
                  (!eventData.bookRedirectLink || eventData.bookRedirectLink.trim() === "")
                    ? t("eventDetails.payment.bookRedirectLinkRequired")
                    : fieldErrors.bookRedirectLink
                }
                requiredIndicator
              />
            )}

            <TicketsList eventData={eventData} isNewEvent={isNewEvent} />
          </BlockStack>
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
